import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { ToastProvider } from './contexts/ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import ExitIntentModal from './components/ExitIntentModal';
import './services/analytics';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Contact from './pages/Contact';
import Begravningar from './pages/Begravningar';
import Blombud from './pages/Blombud';
import LeveransRetur from './pages/LeveransRetur';
import FAQ from './pages/FAQ';
import AdminLogin from './pages/AdminLogin';
import Admin from './pages/Admin';
import ThankYou from './pages/ThankYou';
import SEOCategoryPage from './pages/seo/SEOCategoryPage';
import SEOProductCollectionPage from './pages/seo/SEOProductCollectionPage';
import Julpynt from './pages/Julpynt';
import MajasCottageProducts from './pages/MajasCottageProducts';
import MarketingPage from './pages/MarketingPage.js';
import FloristBlogList from './pages/FloristBlogList';
import FloristBlogPost from './pages/FloristBlogPost';

const App: React.FC = () => {
  // Initialize analytics
  useEffect(() => {
    // Analytics is automatically initialized when imported
    console.log('Analytics tracking initialized');
    
    // SECURITY: Clean any manipulated cart data on app startup
    const cleanManipulatedCart = () => {
      try {
        const storedCart = localStorage.getItem('lillaflora_cart');
        if (storedCart) {
          const parsedCart = JSON.parse(storedCart);
          
          // Check for manipulated data (old structure with prices)
          const hasManipulatedData = Array.isArray(parsedCart) && parsedCart.some((item: any) => 
            item.price !== undefined || 
            item.final_price !== undefined || 
            item.original_price !== undefined ||
            item.name !== undefined ||
            typeof item.price === 'number' ||
            typeof item.final_price === 'number'
          );
          
          if (hasManipulatedData) {
            console.warn('🛡️ SECURITY: Detected manipulated cart data. Cleaning to secure format...');
            // Extract only safe data (ID and quantity)
            const cleanCart = parsedCart
              .filter((item: any) => item.id && typeof item.id === 'number' && item.quantity && typeof item.quantity === 'number')
              .map((item: any) => ({ 
                id: item.id, 
                quantity: item.quantity 
              }));
            
            localStorage.setItem('lillaflora_cart', JSON.stringify(cleanCart));
            console.log('✅ Cart data cleaned. Prices will be fetched securely from server.');
          }
        }
      } catch (error) {
        console.error('Error cleaning cart on startup:', error);
        localStorage.removeItem('lillaflora_cart');
      }
    };
    
    cleanManipulatedCart();
    
    // SECURITY: Check for manipulation when user returns to tab
    const handleFocus = () => {
      cleanManipulatedCart();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Public page wrapper with header and footer
  const PublicPageLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );

  return (
    <AuthProvider>
      <CartProvider>
        <ToastProvider>
        <div className="App">
          <ScrollToTop />
          <Routes>
            {/* Public routes with banner, header and footer */}
          <Route path="/" element={<PublicPageLayout><Home /></PublicPageLayout>} />
          <Route path="/produkter" element={<PublicPageLayout><Products /></PublicPageLayout>} />
          <Route path="/produkter/:id" element={<PublicPageLayout><ProductDetail /></PublicPageLayout>} />
          <Route path="/kundvagn" element={<PublicPageLayout><Cart /></PublicPageLayout>} />
          <Route path="/kassa" element={<PublicPageLayout><Checkout /></PublicPageLayout>} />
          <Route path="/tack" element={<PublicPageLayout><ThankYou /></PublicPageLayout>} />
          <Route path="/kontakt" element={<PublicPageLayout><Contact /></PublicPageLayout>} />
          <Route path="/begravningar" element={<PublicPageLayout><Begravningar /></PublicPageLayout>} />
          <Route path="/blombud" element={<PublicPageLayout><Blombud /></PublicPageLayout>} />
          <Route path="/leverans-retur" element={<PublicPageLayout><LeveransRetur /></PublicPageLayout>} />
          <Route path="/faq" element={<PublicPageLayout><FAQ /></PublicPageLayout>} />
          <Route path="/julpynt" element={<PublicPageLayout><Julpynt /></PublicPageLayout>} />
          <Route path="/majas-cottage-produkter" element={<MajasCottageProducts />} />

          {/* Blog pages */}
          <Route path="/floristbloggen" element={<PublicPageLayout><FloristBlogList /></PublicPageLayout>} />
          <Route path="/floristbloggen/:slug" element={<PublicPageLayout><FloristBlogPost /></PublicPageLayout>} />

          {/* Marketing pages */}
          <Route path="/:slug" element={<PublicPageLayout><MarketingPage /></PublicPageLayout>} />

          {/* SEO-friendly landing pages (no header/footer for performance) */}
          <Route path="/kategori/:categorySlug" element={<SEOCategoryPage />} />
          <Route path="/kollektion/:collection" element={<SEOProductCollectionPage />} />

          {/* Admin login route (no header/footer) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          
          {/* Protected admin routes (no header/footer) */}
          <Route path="/admin/*" element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          } />
          </Routes>
          <ExitIntentModal />
        </div>
        </ToastProvider>
      </CartProvider>
    </AuthProvider>
  );
};

export default App;
